import React, { useRef, useState, useLayoutEffect, useEffect, useCallback } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { T } from "./constants";

// ISP-9999 (gordon-0913-gallery-theme-presets-looping-carousel): the "Preset
// looks" row grew from 3 static tiles to 10-12, so it now scrolls as a
// horizontal, looping carousel instead of wrapping onto extra grid rows.
// Reuses the exact preset-tile visual (wallpaper swatch + accent dot + label,
// purple ring on the active preset) from the old static Grid — only the
// layout/navigation chrome around it is new.
//
// ISP-9999 (gordon-0913-gallery-theme-carousel-paging-per-view-and-cutoff):
// a fixed 92px card width meant the pager counted CARDS while the viewport
// only ever showed ~2.7 of them (a cut-off third card at rest, a cut-off
// first card after one step) and the counter/dots were meaningless as a
// result. A "page" is now one full viewport of whole cards: cardsPerPage is
// derived from the measured scroll-container width, card width is computed
// to exactly fill that container with no remainder, and the arrows/keyboard/
// counter/dots all operate on pages instead of individual cards.

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const CARD_GAP = 10;

// Breakpoints anchored on the original 92px single-card design width: each
// tier keeps cards no narrower than ~90px so the preset swatch/label stay
// legible, while fitting as many whole cards as the container allows.
const CARDS_PER_PAGE_BREAKPOINTS = [
  { minWidth: 300, count: 3 },
  { minWidth: 190, count: 2 },
  { minWidth: 0, count: 1 },
];

const computeCardsPerPage = (containerWidth) => {
  const tier = CARDS_PER_PAGE_BREAKPOINTS.find((b) => containerWidth >= b.minWidth);
  return tier ? tier.count : 1;
};

// Sized so exactly `cardsPerPage` cards + their gaps fill the container with
// nothing left over — the third (or Nth) card can never be a partial sliver.
const computeCardWidth = (containerWidth, cardsPerPage) =>
  (containerWidth - (cardsPerPage - 1) * CARD_GAP) / cardsPerPage;

const NAV_BUTTON_SX = {
  flexShrink: 0, width: 32, height: 32, minWidth: 32, minHeight: 32,
  borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", border: `1px solid ${T.line}`,
  "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
};

const PresetCarousel = ({ presets, activeId, onSelect }) => {
  const cardRefs = useRef([]);
  const scrollRef = useRef(null);
  const didInitPageRef = useRef(false);

  const [containerWidth, setContainerWidth] = useState(0);
  const cardsPerPage = computeCardsPerPage(containerWidth);
  const cardWidth = computeCardWidth(containerWidth, cardsPerPage);
  const pageCount = Math.max(1, Math.ceil(presets.length / cardsPerPage));

  const [pageIndex, setPageIndex] = useState(0);

  // Measure the actual scroll container (not the viewport) so cardsPerPage
  // reflects the panel's real width — 3 at the desktop panel width, 2 at
  // narrower widths, 1 once the stacked-mobile column gets tight. Runs before
  // paint (useLayoutEffect) so there's no flash of the wrong card count.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const measure = () => {
      const width = el.clientWidth;
      setContainerWidth(width);
      if (!didInitPageRef.current) {
        didInitPageRef.current = true;
        const initialCardsPerPage = computeCardsPerPage(width);
        const activeIndex = presets.findIndex((p) => p.id === activeId);
        setPageIndex(Math.floor((activeIndex >= 0 ? activeIndex : 0) / initialCardsPerPage));
      }
    };

    measure();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If a resize shrinks the page count out from under the current page,
  // clamp back onto the new last page instead of pointing past the end.
  useEffect(() => {
    setPageIndex((p) => Math.min(p, pageCount - 1));
  }, [pageCount]);

  const scrollToPage = useCallback((page) => {
    const el = scrollRef.current;
    if (el && el.scrollTo) {
      el.scrollTo({ left: page * el.clientWidth, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
  }, []);

  // Moves the carousel to `page` (wrapping into range) — the loop wraps
  // page-to-page, so the last page's next-page goes to the first page.
  const goToPage = useCallback((page, { focus = true } = {}) => {
    const wrapped = ((page % pageCount) + pageCount) % pageCount;
    setPageIndex(wrapped);
    scrollToPage(wrapped);
    if (focus) {
      const el = cardRefs.current[wrapped * cardsPerPage];
      if (el) el.focus();
    }
  }, [pageCount, cardsPerPage, scrollToPage]);

  // Moves real DOM focus to a specific card (used only for the Tab-wrap
  // boundary cases below, which re-enter at a specific first/last card
  // rather than a page).
  const goToCard = useCallback((index, { focus = true } = {}) => {
    const wrapped = (index + presets.length) % presets.length;
    const page = Math.floor(wrapped / cardsPerPage);
    setPageIndex(page);
    scrollToPage(page);
    if (focus) {
      const el = cardRefs.current[wrapped];
      if (el) el.focus();
    }
  }, [presets.length, cardsPerPage, scrollToPage]);

  const handleCardKeyDown = (e, index) => {
    const isFirst = index === 0;
    const isLast = index === presets.length - 1;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(presets[index].id);
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      goToPage(Math.floor(index / cardsPerPage) + 1);
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goToPage(Math.floor(index / cardsPerPage) - 1);
      return;
    }
    // Looping (Gordon): Tab off the LAST card re-enters at the FIRST; Shift+Tab
    // off the FIRST re-enters at the LAST. Non-boundary cards keep native Tab
    // order (next/previous sibling card) — nothing to intercept there.
    if (e.key === "Tab" && !e.shiftKey && isLast) {
      e.preventDefault();
      goToCard(0);
      return;
    }
    if (e.key === "Tab" && e.shiftKey && isFirst) {
      e.preventDefault();
      goToCard(presets.length - 1);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Tooltip title="Previous preset" arrow>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Previous preset look"
            onClick={() => goToPage(pageIndex - 1, { focus: false })}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), goToPage(pageIndex - 1, { focus: false }))}
            sx={NAV_BUTTON_SX}
          >
            <ChevronLeftRoundedIcon sx={{ color: T.ink }} />
          </Box>
        </Tooltip>

        <Box
          ref={scrollRef}
          role="group"
          aria-label="Preset looks carousel"
          sx={{
            display: "flex", gap: `${CARD_GAP}px`, overflowX: "auto", scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch", flex: 1, minWidth: 0, py: "2px", scrollBehavior: "smooth",
            "@media (prefers-reduced-motion: reduce)": { scrollBehavior: "auto" },
            "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none",
          }}
        >
          {presets.map((p, index) => {
            const active = activeId === p.id;
            // Snap points sit only at page boundaries (the first card of each
            // page) so a swipe settles on a whole page, not a partial one.
            const isPageStart = index % cardsPerPage === 0;
            return (
              <Box
                key={p.id}
                ref={(el) => { cardRefs.current[index] = el; }}
                role="button"
                tabIndex={0}
                aria-label={`Preset ${p.label}`}
                aria-pressed={active}
                onClick={() => onSelect(p.id)}
                onFocus={() => setPageIndex(Math.floor(index / cardsPerPage))}
                onKeyDown={(e) => handleCardKeyDown(e, index)}
                style={{ width: `${cardWidth}px` }}
                sx={{
                  flex: "0 0 auto", scrollSnapAlign: isPageStart ? "start" : "none",
                  border: active ? `2px solid ${T.primary}` : `1px solid ${T.line}`,
                  borderRadius: "12px", p: "8px", cursor: "pointer", background: "#fff",
                  "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ height: 56, borderRadius: "8px", background: p.wallpaper, position: "relative", mb: "8px" }}>
                  <Box sx={{ position: "absolute", left: 8, bottom: 8, width: 18, height: 18, borderRadius: "50%", background: p.accent, border: "2px solid #fff" }} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink, fontFamily: T.fontBody, textAlign: "center" }}>
                  {p.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Tooltip title="Next preset" arrow>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Next preset look"
            onClick={() => goToPage(pageIndex + 1, { focus: false })}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), goToPage(pageIndex + 1, { focus: false }))}
            sx={NAV_BUTTON_SX}
          >
            <ChevronRightRoundedIcon sx={{ color: T.ink }} />
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "2px", mt: "4px" }}>
        <Typography aria-live="polite" sx={{ fontSize: 12, color: T.muted, fontFamily: T.fontBody, mr: "6px" }}>
          {pageIndex + 1} / {pageCount}
        </Typography>
        <Box role="tablist" aria-label="Preset position" sx={{ display: "flex" }}>
          {Array.from({ length: pageCount }, (_, page) => (
            <Box
              key={page}
              role="tab"
              aria-selected={page === pageIndex}
              aria-label={`Go to preset page ${page + 1}`}
              tabIndex={-1}
              onClick={() => goToPage(page, { focus: false })}
              sx={{ width: 20, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: page === pageIndex ? T.primary : T.line }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PresetCarousel;
